import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  Alert,
  Link,
  Grid,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  Star as ReviewIcon,
  Help as QuestionIcon,
  Photo as ImageIcon,
  Work as ServiceIcon,
  Settings as AttributeIcon,
  Event as DateIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

import ImageGallery from '../ImageGallery';
import CategoryManager from '../CategoryManager';
import AttributeManager from '../AttributeManager';
import PendingChangesManager from '../PendingChangesManager';

const OptimizationTable = ({ businessData, reviewsData, onFieldUpdate, optimizationSuggestions = [], locationId, pendingChangesCountProp = null }) => {
  const [editDialog, setEditDialog] = useState({ open: false, field: null, value: '' });
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [attributeManagerOpen, setAttributeManagerOpen] = useState(false);
  const [pendingChangesOpen, setPendingChangesOpen] = useState(false);
  const [pendingChangesCount, setPendingChangesCount] = useState(0);

  // Function to reset all pending changes
  const resetAllPendingChanges = async () => {
    if (locationId) {
      try {
        console.log('🔄 Resetting all pending changes from OptimizationTable...');
        const response = await fetch(`/api/pending-changes/${locationId}/reset`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        
        const result = await response.json();
        console.log('🔄 Reset response:', result);
        
        if (result.success) {
          console.log('✅ Reset completed from OptimizationTable');
          setPendingChangesCount(0);
          await loadPendingChangesCount();
        } else {
          console.error('❌ Reset failed:', result.error);
        }
      } catch (error) {
        console.error('❌ Error resetting:', error);
      }
    }
  };

  // Expose reset function globally for parent components
  React.useEffect(() => {
    window.resetPendingChanges = resetAllPendingChanges;
    return () => {
      delete window.resetPendingChanges;
    };
  }, [locationId]);

  // Debug: Log business data structure to help with troubleshooting
  React.useEffect(() => {
    if (businessData) {
      console.log('📊 OptimizationTable received businessData:', {
        categories: businessData.categories,
        categoriesType: typeof businessData.categories,
        categoriesIsArray: Array.isArray(businessData.categories),
        services: businessData.services,
        servicesType: typeof businessData.services,
        servicesIsArray: Array.isArray(businessData.services),
        phone_numbers: businessData.phone_numbers,
        phoneNumbersType: typeof businessData.phone_numbers,
        phoneNumbersIsArray: Array.isArray(businessData.phone_numbers)
      });
    }
  }, [businessData]);

  // Load pending changes count
  const loadPendingChangesCount = async () => {
    if (locationId) {
      try {
        const response = await fetch(`/api/pending-changes/${locationId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        const data = await response.json();
        if (data.success && data.hasPendingChanges) {
          setPendingChangesCount(data.changes.length);
        } else {
          setPendingChangesCount(0);
        }
      } catch (error) {
        console.error('Error loading pending changes count:', error);
        setPendingChangesCount(0);
      }
    }
  };

  // Load pending changes count on mount and when locationId changes
  React.useEffect(() => {
    loadPendingChangesCount();
  }, [locationId]);

  // Listen for reset events and manual updates (NO MORE POLLING!)
  React.useEffect(() => {
    const handleReset = () => {
      console.log('🔄 Received reset event, updating pending changes count immediately');
      setPendingChangesCount(0);
      loadPendingChangesCount();
    };

    const handlePendingChangesUpdate = (event) => {
      console.log('🔄 Received pending changes update event');
      loadPendingChangesCount();
    };

    window.addEventListener('pendingChangesReset', handleReset);
    window.addEventListener('pendingChangesUpdated', handlePendingChangesUpdate);

    return () => {
      window.removeEventListener('pendingChangesReset', handleReset);
      window.removeEventListener('pendingChangesUpdated', handlePendingChangesUpdate);
    };
  }, [locationId]);

  // Define all business profile fields that should be tracked
  const businessFields = [
    {
      id: 'reviews',
      label: 'Bewertungen',
      icon: <ReviewIcon />,
      category: 'engagement',
      getValue: (data) => {
        // Prioritize reviewsData from API call first
        if (reviewsData?.statistics) {
          const count = reviewsData.statistics.totalReviews || 0;
          const rating = reviewsData.statistics.averageRating || 0;
          
          if (count > 0 && rating > 0) {
            return `${rating.toFixed(1)} Sterne (${count} Bewertungen)`;
          } else if (count > 0) {
            return `${count} Bewertungen (Keine Durchschnittsbewertung)`;
          } else {
            return 'Keine Bewertungen';
          }
        }
        
        // Fallback to businessData sources
        let count = 0;
        let rating = 0;
        
        if (data?.reviews?.count !== undefined) {
          count = data.reviews.count;
          rating = data.reviews.avg_rating || 0;
        } else if (data?.userRatingsTotal !== undefined) {
          count = data.userRatingsTotal;
          rating = data.rating || 0;
        } else if (data?.rating !== undefined && data?.user_ratings_total !== undefined) {
          count = data.user_ratings_total;
          rating = data.rating;
        }
        
        if (count > 0 && rating > 0) {
          return `${rating.toFixed(1)} Sterne (${count} Bewertungen)`;
        } else if (count > 0) {
          return `${count} Bewertungen (Bewertung wird geladen...)`;
        } else {
          return 'Keine Bewertungen';
        }
      },
      getStatus: (data) => {
        // Prioritize reviewsData from API call first
        if (reviewsData?.statistics) {
          const count = reviewsData.statistics.totalReviews || 0;
          const rating = reviewsData.statistics.averageRating || 0;
          
          if (count === 0) return 'error';
          if (rating < 4.0) return 'warning';
          return 'success';
        }
        
        // Fallback to businessData sources
        let count = 0;
        let rating = 0;
        
        if (data?.reviews?.count !== undefined) {
          count = data.reviews.count;
          rating = data.reviews.avg_rating || 0;
        } else if (data?.userRatingsTotal !== undefined) {
          count = data.userRatingsTotal;
          rating = data.rating || 0;
        } else if (data?.rating !== undefined && data?.user_ratings_total !== undefined) {
          count = data.user_ratings_total;
          rating = data.rating;
        }
        
        if (count === 0) return 'error';
        if (rating < 4.0) return 'warning';
        return 'success';
      },
      editable: false,
      hasDetails: true
    },
    {
      id: 'questions',
      label: 'Fragen & Antworten',
      icon: <QuestionIcon />,
      category: 'engagement',
      getValue: (data) => {
        // Handle new array structure from Google Business Profile API
        const questions = data?.questions || [];
        const count = Array.isArray(questions) ? questions.length : (data?.questions?.count || 0);
        
        if (count > 0) {
          const answeredCount = Array.isArray(questions) 
            ? questions.filter(q => q.topAnswers && q.topAnswers.length > 0).length 
            : 0;
          return `${count} Fragen${answeredCount > 0 ? ` (${answeredCount} beantwortet)` : ''}`;
        }
        return 'Keine Fragen';
      },
      getStatus: (data) => {
        const questions = data?.questions || [];
        const count = Array.isArray(questions) ? questions.length : (data?.questions?.count || 0);
        
        if (count === 0) return 'warning';
        
        const answeredCount = Array.isArray(questions) 
          ? questions.filter(q => q.topAnswers && q.topAnswers.length > 0).length 
          : 0;
        
        return answeredCount > 0 ? 'success' : 'warning';
      },
      editable: false
    },
    {
      id: 'images',
      label: 'Bilder',
      icon: <ImageIcon />,
      category: 'media',
      getValue: (data) => {
        // Enhanced media debugging
        console.log('🔍 FRONTEND DEBUG: Media getValue called with data:', {
          hasMediaItems: !!data?.media_items,
          mediaItemsLength: data?.media_items?.length || 0,
          hasMedia: !!data?.media,
          mediaLength: data?.media?.length || 0,
          hasPhotos: !!data?.photos,
          photosLength: data?.photos?.length || 0,
          hasMediaProfile: !!data?.media_profile,
          hasMediaCover: !!data?.media_cover,
          dataKeys: Object.keys(data || {}),
          timestamp: new Date().toISOString()
        });
        
        // Check multiple possible image data sources
        let count = 0;
        let hasProfilePhoto = false;
        let hasCoverPhoto = false;
        let sourceImages = [];
        
        // Try different data structures for images
        if (Array.isArray(data?.media_items)) {
          count = data.media_items.length;
          sourceImages = data.media_items;
          console.log('🔍 FRONTEND DEBUG: Using media_items array', {
            count,
            firstItem: data.media_items[0] ? {
              name: data.media_items[0].name,
              hasGoogleUrl: !!data.media_items[0].googleUrl,
              hasThumbnailUrl: !!data.media_items[0].thumbnailUrl,
              hasProxyThumbnailUrl: !!data.media_items[0].proxyThumbnailUrl,
              hasProxyGoogleUrl: !!data.media_items[0].proxyGoogleUrl
            } : null
          });
        } else if (Array.isArray(data?.media)) {
          count = data.media.length;
          sourceImages = data.media;
          console.log('🔍 FRONTEND DEBUG: Using media array', { count });
        } else if (Array.isArray(data?.photos)) {
          count = data.photos.length;
          sourceImages = data.photos;
          console.log('🔍 FRONTEND DEBUG: Using photos array', { count });
        }
        
        // Check for specific photo types
        hasProfilePhoto = !!(data?.media_profile || data?.profile_photo);
        hasCoverPhoto = !!(data?.media_cover || data?.cover_photo);
        
        console.log('🔍 FRONTEND DEBUG: Media analysis result', {
          totalCount: count,
          hasProfilePhoto,
          hasCoverPhoto,
          sourceImagesLength: sourceImages.length,
          willShowImages: count > 0
        });
        
        if (count > 0) {
          let details = `${count} Bilder`;
          if (hasProfilePhoto) details += ', Profilbild ✓';
          if (hasCoverPhoto) details += ', Titelbild ✓';
          return { text: details, hasImages: true, images: sourceImages };
        } else {
          return { text: 'Keine Bilder', hasImages: false, images: [] };
        }
      },
      getStatus: (data) => {
        // Check multiple possible image data sources
        let count = 0;
        let hasProfilePhoto = false;
        
        if (Array.isArray(data?.media_items)) {
          count = data.media_items.length;
        } else if (Array.isArray(data?.media)) {
          count = data.media.length;
        } else if (Array.isArray(data?.photos)) {
          count = data.photos.length;
        }
        
        hasProfilePhoto = !!(data?.media_profile || data?.profile_photo);
        
        if (count === 0) return 'error';
        if (count < 3 || !hasProfilePhoto) return 'warning';
        return 'success';
      },
      editable: false,
      hasDetails: true
    },
    {
      id: 'name',
      label: 'Name',
      icon: <BusinessIcon />,
      category: 'basic',
      getValue: (data) => data?.business_name || data?.locationName || 'Nicht verfügbar',
      getStatus: (data) => (data?.business_name || data?.locationName) ? 'success' : 'error',
      editable: true,
      editType: 'text'
    },
    {
      id: 'description',
      label: 'Beschreibung',
      icon: <DescriptionIcon />,
      category: 'basic',
      getValue: (data) => {
        const desc = data?.business_description || data?.description || '';
        if (!desc) return 'Keine Beschreibung';
        
        // Crop text to maintain consistent row height
        const maxLength = 120;
        return desc.length > maxLength ? desc.substring(0, maxLength) + '...' : desc;
      },
      getStatus: (data) => {
        const desc = data?.business_description || data?.description || '';
        if (!desc) return 'error';
        if (desc.length < 50) return 'warning';
        return 'success';
      },
      editable: true,
      editType: 'textarea'
    },
    {
      id: 'category',
      label: 'Kategorie',
      icon: <CategoryIcon />,
      category: 'basic',
      getValue: (data) => {
        // Handle both array and object formats for categories
        let categories = [];
        
        if (Array.isArray(data?.categories)) {
          // Array format: [{displayName: "...", label: "...", categoryId: "..."}, ...]
          categories = data.categories;
        } else if (data?.categories?.primaryCategory) {
          // Object format: {primaryCategory: {...}, additionalCategories: [...]}
          categories = [data.categories.primaryCategory];
          if (data.categories.additionalCategories) {
            categories.push(...data.categories.additionalCategories);
          }
        } else if (data?.primary_category) {
          // Separate primary category field
          categories = [data.primary_category];
          if (data?.additional_categories) {
            categories.push(...data.additional_categories);
          }
        }
        
        if (categories.length === 0) return 'Keine Kategorien';
        
        const primaryLabel = categories[0]?.displayName || categories[0]?.label || 'Unbekannt';
        if (categories.length === 1) {
          return primaryLabel;
        } else {
          return `${primaryLabel} (+${categories.length - 1} weitere)`;
        }
      },
      getStatus: (data) => {
        // Check for categories in different formats
        let hasCategory = false;
        
        if (Array.isArray(data?.categories) && data.categories.length > 0) {
          hasCategory = true;
        } else if (data?.categories?.primaryCategory) {
          hasCategory = true;
        } else if (data?.primary_category) {
          hasCategory = true;
        }
        
        return hasCategory ? 'success' : 'error';
      },
      editable: true,
      editType: 'category'
    },
    {
      id: 'services',
      label: 'Dienstleistungen',
      icon: <ServiceIcon />,
      category: 'basic',
      getValue: (data) => {
        const services = Array.isArray(data?.services) ? data.services : [];
        const count = services.length;
        return count > 0 ? `${count} Dienstleistungen` : 'Keine Dienstleistungen';
      },
      getStatus: (data) => {
        const services = Array.isArray(data?.services) ? data.services : [];
        const count = services.length;
        return count > 0 ? 'success' : 'warning';
      },
      editable: true,
      editType: 'services'
    },
    {
      id: 'attributes',
      label: 'Attribute',
      icon: <AttributeIcon />,
      category: 'basic',
      getValue: (data) => {
        // Handle both old format (object) and new format (array)
        let attributeCount = 0;
        let attributeNames = [];
        
        if (Array.isArray(data?.attributes)) {
          // New format: array of attribute objects
          attributeCount = data.attributes.length;
          attributeNames = data.attributes.map(attr => attr.label || attr.key).slice(0, 3);
        } else if (data?.attributes && typeof data.attributes === 'object') {
          // Old format: object with key-value pairs
          attributeCount = Object.keys(data.attributes).length;
          attributeNames = Object.keys(data.attributes).slice(0, 3);
        }
        
        if (attributeCount === 0) return 'Keine Attribute';
        
        const displayText = attributeNames.length > 0 ? 
          attributeNames.join(', ') + (attributeCount > 3 ? ` (+${attributeCount - 3} weitere)` : '') :
          `${attributeCount} Attribute`;
          
        return { text: displayText, count: attributeCount, attributes: data?.attributes || [] };
      },
      getStatus: (data) => {
        let count = 0;
        if (Array.isArray(data?.attributes)) {
          count = data.attributes.length;
        } else if (data?.attributes && typeof data.attributes === 'object') {
          count = Object.keys(data.attributes).length;
        }
        
        if (count === 0) return 'warning';
        if (count < 5) return 'warning';
        return 'success';
      },
      editable: true,
      editType: 'attributes',
      hasDetails: true
    },
    {
      id: 'address',
      label: 'Adresse',
      icon: <LocationIcon />,
      category: 'contact',
      getValue: (data) => data?.business_address || data?.address?.formattedAddress || 'Keine Adresse',
      getStatus: (data) => (data?.business_address || data?.address?.formattedAddress) ? 'success' : 'error',
      editable: true,
      editType: 'text'
    },
    {
      id: 'opening_date',
      label: 'Eröffnungsdatum',
      icon: <DateIcon />,
      category: 'basic',
      getValue: (data) => {
        const date = data?.open_info?.opening_date;
        if (!date) return 'Nicht verfügbar';
        return `${date.day}.${date.month}.${date.year}`;
      },
      getStatus: (data) => data?.open_info?.opening_date ? 'success' : 'warning',
      editable: true,
      editType: 'date'
    },
    {
      id: 'hours',
      label: 'Öffnungszeiten',
      icon: <ScheduleIcon />,
      category: 'contact',
      getValue: (data) => {
        const hours = data?.open_hours || data?.businessHours;
        if (!hours) return 'Keine Öffnungszeiten';
        
        // Handle our format: { monday: { open: "09:00", close: "13:00", split: true, afternoon_open: "14:00" } }
        const daysWithHours = Object.keys(hours).filter(day => {
          const dayHours = hours[day];
          return dayHours && (dayHours.open || dayHours.intervals?.length > 0);
        }).length;
        
        if (daysWithHours === 0) return 'Keine Öffnungszeiten';
        
        // Show detailed hours for current day
        const today = new Date().toLocaleDateString('de-DE', { weekday: 'long' }).toLowerCase();
        const todayHours = hours[today];
        
        if (todayHours && todayHours.open) {
          if (todayHours.split && todayHours.afternoon_open) {
            return `Heute: ${todayHours.open}-${todayHours.close}, ${todayHours.afternoon_open}-${todayHours.afternoon_close} (${daysWithHours} Tage)`;
          } else {
            return `Heute: ${todayHours.open}-${todayHours.close} (${daysWithHours} Tage)`;
          }
        }
        
        return `${daysWithHours} Tage definiert`;
      },
      getStatus: (data) => {
        const hours = data?.open_hours || data?.businessHours;
        if (!hours) return 'error';
        
        const daysWithHours = Object.keys(hours).filter(day => {
          const dayHours = hours[day];
          return dayHours && (dayHours.open || dayHours.intervals?.length > 0);
        }).length;
        
        if (daysWithHours === 0) return 'error';
        if (daysWithHours >= 5) return 'success';
        return 'warning';
      },
      editable: true,
      editType: 'hours',
      excludeFromAI: true
    },
    {
      id: 'website',
      label: 'Website URL',
      icon: <WebsiteIcon />,
      category: 'contact',
      getValue: (data) => data?.website_uri || data?.websiteUrl || 'Keine Website',
      getStatus: (data) => (data?.website_uri || data?.websiteUrl) ? 'success' : 'warning',
      editable: true,
      editType: 'url'
    },
    {
      id: 'phone',
      label: 'Telefonnummer',
      icon: <PhoneIcon />,
      category: 'contact',
      getValue: (data) => {
        const phoneNumbers = Array.isArray(data?.phone_numbers) ? data.phone_numbers : [];
        return phoneNumbers[0] || data?.phoneNumber || 'Keine Telefonnummer';
      },
      getStatus: (data) => {
        const phoneNumbers = Array.isArray(data?.phone_numbers) ? data.phone_numbers : [];
        return (phoneNumbers[0] || data?.phoneNumber) ? 'success' : 'warning';
      },
      editable: true,
      editType: 'phone'
    },
    // Social Media Fields
    {
      id: 'instagram',
      label: 'Instagram URL',
      icon: <InstagramIcon />,
      category: 'social',
      getValue: (data) => data?.attributes?.['attributes/url_instagram'] || 'Keine Instagram URL',
      getStatus: (data) => data?.attributes?.['attributes/url_instagram'] ? 'success' : 'warning',
      editable: true,
      editType: 'url'
    },
    {
      id: 'facebook',
      label: 'Facebook URL',
      icon: <FacebookIcon />,
      category: 'social',
      getValue: (data) => data?.attributes?.['attributes/url_facebook'] || 'Keine Facebook URL',
      getStatus: (data) => data?.attributes?.['attributes/url_facebook'] ? 'success' : 'warning',
      editable: true,
      editType: 'url'
    },
    {
      id: 'linkedin',
      label: 'LinkedIn URL',
      icon: <LinkedInIcon />,
      category: 'social',
      getValue: (data) => data?.attributes?.['attributes/url_linkedin'] || 'Keine LinkedIn URL',
      getStatus: (data) => data?.attributes?.['attributes/url_linkedin'] ? 'success' : 'warning',
      editable: true,
      editType: 'url'
    },
    {
      id: 'youtube',
      label: 'YouTube URL',
      icon: <YouTubeIcon />,
      category: 'social',
      getValue: (data) => data?.attributes?.['attributes/url_youtube'] || 'Keine YouTube URL',
      getStatus: (data) => data?.attributes?.['attributes/url_youtube'] ? 'success' : 'warning',
      editable: true,
      editType: 'url'
    }
  ];

  const getStatusChip = (status, field) => {
    const suggestion = optimizationSuggestions.find(s => s.field === field.id || s.type === field.id);
    
    const statusConfig = {
      success: { 
        label: 'Vollständig', 
        color: '#22c55e', 
        bgcolor: '#f0fdf4',
        icon: <CheckIcon fontSize="small" />
      },
      warning: { 
        label: suggestion ? 'KI-Empfehlung' : 'Unvollständig', 
        color: suggestion ? '#2196f3' : '#f59e0b', 
        bgcolor: suggestion ? '#e3f2fd' : '#fef3c7',
        icon: suggestion ? <CheckIcon fontSize="small" /> : <WarningIcon fontSize="small" />
      },
      error: { 
        label: suggestion ? 'KI-Empfehlung' : 'Fehlend', 
        color: suggestion ? '#2196f3' : '#ef4444', 
        bgcolor: suggestion ? '#e3f2fd' : '#fef2f2',
        icon: suggestion ? <CheckIcon fontSize="small" /> : <ErrorIcon fontSize="small" />
      }
    };

    const config = statusConfig[status] || statusConfig.warning;
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        size="small"
        sx={{ 
          color: config.color,
          bgcolor: config.bgcolor,
          border: `1px solid ${config.color}`,
          fontWeight: 500,
          minWidth: 120
        }}
      />
    );
  };

  const handleEdit = (field) => {
    // Handle categories with special dialog
    if (field.id === 'category') {
      setCategoryManagerOpen(true);
      return;
    }
    
    // Handle attributes with special dialog
    if (field.id === 'attributes') {
      setAttributeManagerOpen(true);
      return;
    }
    
    let currentValue;
    
    // For description field, get the full uncropped text directly from businessData
    if (field.id === 'description') {
      currentValue = businessData?.business_description || businessData?.description || '';
    } else {
      currentValue = field.getValue(businessData);
    }
    
    // Handle object values (like images)
    if (typeof currentValue === 'object' && currentValue.text) {
      currentValue = currentValue.text;
    }
    
    setEditDialog({
      open: true,
      field: field,
      value: currentValue === 'Nicht verfügbar' || currentValue.startsWith('Keine') ? '' : currentValue
    });
  };

  const handleSave = async () => {
    if (editDialog.field && locationId) {
      try {
        // Speichere Änderung als "pending" statt direkt zu Google zu senden
        const currentValue = editDialog.field.getValue(businessData);
        
        const response = await fetch(`/api/pending-changes/${locationId}/${editDialog.field.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            value: editDialog.value,
            oldValue: typeof currentValue === 'object' && currentValue.text ? currentValue.text : currentValue
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          console.log('✅ Change saved as pending:', result.message);
          setEditDialog({ open: false, field: null, value: '' });
          
          // Update pending changes count
          setPendingChangesCount(result.pendingChangesCount || 0);
          
          // Trigger pending changes update event
          window.dispatchEvent(new CustomEvent('pendingChangesUpdated'));
          
          // Optional: Trigger refresh of businessData if onFieldUpdate exists
          if (onFieldUpdate) {
            onFieldUpdate(editDialog.field.id, editDialog.value);
          }
          
          // Show user that change was saved locally
          // You could add a toast notification here
        } else {
          throw new Error(result.error || 'Failed to save change');
        }
      } catch (error) {
        console.error('Error saving field as pending:', error);
        // Keep dialog open on error
      }
    }
  };

  const handleCategorySave = async (categories) => {
    if (locationId) {
      try {
        // Get current categories for comparison
        const currentCategories = getCurrentCategories();
        
        // Convert categories to the format expected by the backend
        const categoryData = {
          primaryCategory: categories[0] || null,
          additionalCategories: categories.slice(1)
        };
        
        // Save as pending change
        const response = await fetch(`/api/pending-changes/${locationId}/category`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            value: categoryData,
            oldValue: currentCategories
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          console.log('✅ Categories saved as pending:', result.message);
          setCategoryManagerOpen(false);
          
          // Update pending changes count
          setPendingChangesCount(result.pendingChangesCount || 0);
          
          // Trigger pending changes update event
          window.dispatchEvent(new CustomEvent('pendingChangesUpdated'));
          
          // Optional: Update local UI if onFieldUpdate exists
          if (onFieldUpdate) {
            onFieldUpdate('category', categoryData);
          }
        } else {
          throw new Error(result.error || 'Failed to save categories');
        }
      } catch (error) {
        console.error('Error saving categories as pending:', error);
      }
    }
  };

  // Get current categories in the right format for CategoryManager
  const getCurrentCategories = () => {
    const categories = [];
    
    // Handle different category data structures
    if (Array.isArray(businessData?.categories)) {
      return businessData.categories;
    } else if (businessData?.categories?.primaryCategory) {
      categories.push(businessData.categories.primaryCategory);
      if (businessData.categories.additionalCategories) {
        categories.push(...businessData.categories.additionalCategories);
      }
    } else if (businessData?.primary_category) {
      categories.push(businessData.primary_category);
      if (businessData?.additional_categories) {
        categories.push(...businessData.additional_categories);
      }
    }
    
    return categories;
  };

  const handleAttributeSave = async (attributes) => {
    if (locationId) {
      try {
        // Get current attributes for comparison
        const currentAttributes = getCurrentAttributes();
        
        // Save as pending change
        const response = await fetch(`/api/pending-changes/${locationId}/attributes`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            value: attributes,
            oldValue: currentAttributes
          })
        });
        
        const result = await response.json();
        
        if (result.success) {
          console.log('✅ Attributes saved as pending:', result.message);
          setAttributeManagerOpen(false);
          
          // Update pending changes count
          setPendingChangesCount(result.pendingChangesCount || 0);
          
          // Trigger pending changes update event
          window.dispatchEvent(new CustomEvent('pendingChangesUpdated'));
          
          // Optional: Update local UI if onFieldUpdate exists
          if (onFieldUpdate) {
            onFieldUpdate('attributes', attributes);
          }
        } else {
          throw new Error(result.error || 'Failed to save attributes');
        }
      } catch (error) {
        console.error('Error saving attributes as pending:', error);
      }
    }
  };

  // Get current attributes in the right format for AttributeManager
  const getCurrentAttributes = () => {
    if (Array.isArray(businessData?.attributes)) {
      return businessData.attributes;
    } else if (businessData?.attributes && typeof businessData.attributes === 'object') {
      // Convert old object format to new array format
      return Object.entries(businessData.attributes).map(([key, value]) => ({
        key: key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        description: value || 'Attribut aktiviert',
        category: 'general'
      }));
    }
    return [];
  };

  const renderEditDialog = () => {
    if (!editDialog.field) return null;

    const suggestion = optimizationSuggestions.find(s => s.field === editDialog.field.id || s.type === editDialog.field.id);
    const hasAISuggestion = !!suggestion && !editDialog.field.excludeFromAI;

    const handleAcceptAISuggestion = () => {
      const aiValue = suggestion.suggestedValue || suggestion.recommendedValue || suggestion.suggestion || '';
      setEditDialog(prev => ({ ...prev, value: aiValue }));
    };

    return (
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, field: null, value: '' })} 
        maxWidth={hasAISuggestion ? "lg" : "md"} 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#fafafa', 
          borderBottom: '1px solid #e5e5e5',
          py: 3,
          px: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color: '#2196f3' }}>{editDialog.field.icon}</Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
              {editDialog.field.label} bearbeiten
            </Typography>
            {hasAISuggestion && (
              <Chip 
                label="KI-Empfehlung verfügbar" 
                size="small" 
                sx={{ 
                  bgcolor: '#e3f2fd', 
                  color: '#1976d2',
                  fontWeight: 500,
                  border: '1px solid #2196f3'
                }}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          {hasAISuggestion ? (
            // Clean Professional Comparison View
            <Box>
              <Typography variant="body1" sx={{ color: '#666', mb: 3, lineHeight: 1.6 }}>
                Unsere KI hat eine Verbesserung für dieses Feld identifiziert. Sie können die Empfehlung übernehmen oder eigene Änderungen vornehmen.
              </Typography>
              
              <Grid container spacing={3}>
                {/* Current Value */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#495057' }}>
                      Aktueller Wert
                    </Typography>
                  
                  {editDialog.field.editType === 'textarea' ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={editDialog.value}
                      onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                      placeholder={`Aktueller ${editDialog.field.label}...`}
                      variant="outlined"
                      size="small"
                    />
                  ) : editDialog.field.editType === 'url' ? (
                    <TextField
                      fullWidth
                      type="url"
                      value={editDialog.value}
                      onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="https://..."
                      variant="outlined"
                      size="small"
                    />
                  ) : editDialog.field.editType === 'phone' ? (
                    <TextField
                      fullWidth
                      type="tel"
                      value={editDialog.value}
                      onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="+49 123 456789"
                      variant="outlined"
                      size="small"
                    />
                  ) : editDialog.field.editType === 'hours' ? (
                    <BusinessHoursEditor 
                      hours={businessData?.open_hours || {}}
                      onChange={(newHours) => setEditDialog(prev => ({ ...prev, value: newHours }))}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      value={editDialog.value}
                      onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                      placeholder={`Aktueller ${editDialog.field.label}...`}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              </Grid>

                {/* AI Recommendation */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ p: 3, bgcolor: '#e3f2fd', borderRadius: 2, border: '2px solid #2196f3' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        KI-Empfehlung
                      </Typography>
                      <Button 
                        size="small" 
                        variant="contained" 
                        onClick={handleAcceptAISuggestion}
                        sx={{ 
                          bgcolor: '#2196f3',
                          color: 'white',
                          fontWeight: 500,
                          px: 2,
                          py: 0.5,
                          textTransform: 'none',
                          '&:hover': { bgcolor: '#1976d2' }
                        }}
                      >
                        Übernehmen
                      </Button>
                    </Box>
                    
                    <Box sx={{ 
                      p: 2.5, 
                      bgcolor: 'white', 
                      borderRadius: 1, 
                      border: '1px solid #2196f3',
                      minHeight: editDialog.field.editType === 'textarea' ? '120px' : '60px',
                      display: 'flex',
                      alignItems: editDialog.field.editType === 'textarea' ? 'flex-start' : 'center'
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#1a1a1a',
                          lineHeight: 1.5,
                          fontWeight: 500
                        }}
                      >
                        {suggestion.suggestedValue || suggestion.recommendedValue || suggestion.suggestion || 'Verbesserungsvorschlag verfügbar'}
                      </Typography>
                    </Box>
                    
                    {(suggestion.seoReason || suggestion.reason || suggestion.description) && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                        <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
                          <strong>Begründung:</strong> {suggestion.seoReason || suggestion.reason || suggestion.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : (
            // Clean Standard View
            <Box>
              <Typography variant="body1" sx={{ color: '#666', mb: 3, lineHeight: 1.6 }}>
                Bearbeiten Sie das Feld "{editDialog.field.label}" für Ihr Google Business Profil.
              </Typography>
              
              <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                {editDialog.field.editType === 'textarea' ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    value={editDialog.value}
                    onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                    placeholder={`${editDialog.field.label} eingeben...`}
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        borderRadius: 2
                      }
                    }}
                  />
                ) : editDialog.field.editType === 'url' ? (
                  <TextField
                    fullWidth
                    type="url"
                    value={editDialog.value}
                    onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="https://..."
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        borderRadius: 2
                      }
                    }}
                  />
                ) : editDialog.field.editType === 'phone' ? (
                  <TextField
                    fullWidth
                    type="tel"
                    value={editDialog.value}
                    onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="+49 123 456789"
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        borderRadius: 2
                      }
                    }}
                  />
                ) : editDialog.field.editType === 'hours' ? (
                  <BusinessHoursEditor 
                    hours={businessData?.open_hours || {}}
                    onChange={(newHours) => setEditDialog(prev => ({ ...prev, value: newHours }))}
                  />
                ) : (
                  <TextField
                    fullWidth
                    value={editDialog.value}
                    onChange={(e) => setEditDialog(prev => ({ ...prev, value: e.target.value }))}
                    placeholder={`${editDialog.field.label} eingeben...`}
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        borderRadius: 2
                      }
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 4, 
          bgcolor: '#fafafa', 
          borderTop: '1px solid #e5e5e5',
          gap: 2,
          justifyContent: 'flex-end'
        }}>
          <Button 
            onClick={() => setEditDialog({ open: false, field: null, value: '' })}
            sx={{
              color: '#666',
              fontWeight: 500,
              px: 3,
              py: 1,
              textTransform: 'none',
              '&:hover': { bgcolor: '#f5f5f5' }
            }}
          >
            Abbrechen
          </Button>
          {hasAISuggestion && (
            <Button 
              variant="outlined" 
              onClick={handleAcceptAISuggestion}
              sx={{
                color: '#2196f3',
                borderColor: '#2196f3',
                fontWeight: 500,
                px: 3,
                py: 1,
                textTransform: 'none',
                '&:hover': { 
                  bgcolor: '#e3f2fd',
                  borderColor: '#1976d2'
                }
              }}
            >
              KI-Empfehlung übernehmen
            </Button>
          )}
          <Button 
            variant="contained" 
            onClick={handleSave}
            sx={{
              bgcolor: '#2196f3',
              color: 'white',
              fontWeight: 600,
              px: 4,
              py: 1,
              textTransform: 'none',
              '&:hover': { bgcolor: '#1976d2' }
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderValue = (field, value) => {
    const suggestion = optimizationSuggestions.find(s => s.field === field.id || s.type === field.id);
    
    // Handle attributes field with special structure
    if (field.id === 'attributes' && typeof value === 'object' && value.text) {
      const isCurrentlyEmpty = value.count === 0;
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: isCurrentlyEmpty ? 'text.secondary' : 'text.primary',
              fontStyle: isCurrentlyEmpty ? 'italic' : 'normal'
            }}
          >
            {value.text}
          </Typography>
          
          {/* KI Suggestion for attributes */}
          {suggestion && (
            <Box sx={{ 
              mt: 1, 
              p: 2, 
              bgcolor: '#e3f2fd', 
              borderRadius: 1, 
              border: '1px solid #2196f3'
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1976d2', display: 'block', mb: 0.5 }}>
                KI-Empfehlung:
              </Typography>
              <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '0.875rem' }}>
                {suggestion.suggestedValue || suggestion.suggestion || 'Weitere Attribute für bessere Auffindbarkeit hinzufügen'}
              </Typography>
            </Box>
          )}
          
          {/* Attribute badges */}
          {value.count > 0 && Array.isArray(value.attributes) && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {value.attributes.slice(0, 3).map((attr, index) => (
                <Chip
                  key={index}
                  label={attr.label || attr.key}
                  size="small"
                  sx={{ 
                    fontSize: '0.7rem',
                    height: 20,
                    bgcolor: '#e8f5e8',
                    color: '#2e7d32'
                  }}
                />
              ))}
              {value.count > 3 && (
                <Chip
                  label={`+${value.count - 3}`}
                  size="small"
                  sx={{ 
                    fontSize: '0.7rem',
                    height: 20,
                    bgcolor: '#f0f0f0',
                    color: '#666'
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      );
    }
    
    // Handle images field with special structure
    if (field.id === 'images' && typeof value === 'object' && value.text) {
      const isCurrentlyEmpty = !value.hasImages;
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: isCurrentlyEmpty ? 'text.secondary' : 'text.primary',
              fontStyle: isCurrentlyEmpty ? 'italic' : 'normal'
            }}
          >
            {value.text}
          </Typography>
          
          {/* KI Suggestion for images */}
          {suggestion && (
            <Alert severity="info" sx={{ py: 0.5, px: 1, fontSize: '0.75rem' }}>
              🤖 KI empfiehlt: {suggestion.suggestedValue || suggestion.suggestion || 'Weitere Bilder hinzufügen für bessere Sichtbarkeit'}
            </Alert>
          )}
          
          {/* Ultra small image thumbnails */}
          {value.hasImages && value.images.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              {value.images.slice(0, 3).map((image, index) => {
                const thumbnailUrl = image.proxyThumbnailUrl || image.thumbnailUrl || image.proxyGoogleUrl || image.googleUrl;
                return (
                  <Box
                    key={index}
                    component="img"
                    src={thumbnailUrl}
                    alt={`Thumbnail ${index + 1}`}
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: 0.5,
                      objectFit: 'cover',
                      border: '1px solid #e0e0e0',
                      backgroundColor: '#f5f5f5'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      console.warn(`Failed to load thumbnail: ${thumbnailUrl}`);
                    }}
                    onLoad={() => {
                      console.log(`Successfully loaded thumbnail: ${thumbnailUrl}`);
                    }}
                  />
                );
              })}
              {value.images.length > 3 && (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 0.5,
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#666',
                    fontWeight: 'bold'
                  }}
                >
                  +{value.images.length - 3}
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    }
    
    // Handle normal fields with AI suggestions
    const isCurrentlyEmpty = value === 'Nicht verfügbar' || value.startsWith('Keine');
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: isCurrentlyEmpty ? 'text.secondary' : 'text.primary',
            fontStyle: isCurrentlyEmpty ? 'italic' : 'normal'
          }}
        >
          {value}
        </Typography>
        
        {/* Clean KI Suggestion Display */}
        {suggestion && (
          <Box sx={{ 
            mt: 1, 
            p: 2, 
            bgcolor: '#e3f2fd', 
            borderRadius: 1, 
            border: '1px solid #2196f3'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1976d2', display: 'block', mb: 0.5 }}>
              KI-Empfehlung:
            </Typography>
            <Typography variant="body2" sx={{ color: '#1a1a1a', fontSize: '0.875rem' }}>
              {suggestion.suggestedValue || suggestion.suggestion || suggestion.recommendedValue || 'Verbesserung verfügbar'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderActions = (field, value) => {
    const suggestion = optimizationSuggestions.find(s => s.field === field.id || s.type === field.id);
    const isCurrentlyEmpty = (typeof value === 'object' && value.text) ? 
      !value.hasImages : 
      (value === 'Nicht verfügbar' || value.startsWith('Keine'));
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
        {field.editable && (
          <Button 
            size="small" 
            variant={suggestion ? "contained" : "outlined"}
            onClick={() => handleEdit(field)}
            sx={{
              color: suggestion ? 'white' : (isCurrentlyEmpty ? '#2196f3' : '#666'),
              bgcolor: suggestion ? '#2196f3' : 'transparent',
              borderColor: suggestion ? '#2196f3' : (isCurrentlyEmpty ? '#2196f3' : '#e0e0e0'),
              fontWeight: 500,
              textTransform: 'none',
              px: 2,
              py: 0.75,
              '&:hover': {
                bgcolor: suggestion ? '#1976d2' : '#f5f5f5',
                borderColor: suggestion ? '#1976d2' : '#2196f3'
              }
            }}
          >
            {isCurrentlyEmpty ? 'Hinzufügen' : 'Bearbeiten'}
          </Button>
        )}
        
        {field.hasDetails && field.id === 'reviews' && locationId && (
          <Button 
            size="small" 
            variant="outlined"
            onClick={() => window.location.href = `/reviews/${locationId}`}
            sx={{
              color: '#666',
              borderColor: '#e0e0e0',
              textTransform: 'none',
              px: 2,
              py: 0.75,
              '&:hover': {
                bgcolor: '#f5f5f5',
                borderColor: '#666'
              }
            }}
          >
            Details
          </Button>
        )}
        {field.hasDetails && field.id === 'images' && (
          <Button 
            size="small" 
            variant="outlined"
            onClick={() => setImageGalleryOpen(true)}
            sx={{
              color: '#666',
              borderColor: '#e0e0e0',
              textTransform: 'none',
              px: 2,
              py: 0.75,
              '&:hover': {
                bgcolor: '#f5f5f5',
                borderColor: '#666'
              }
            }}
          >
            Galerie
          </Button>
        )}
      </Box>
    );
  };

  // Calculate optimization score
  const calculateOptimizationScore = () => {
    const totalFields = businessFields.length;
    const completedFields = businessFields.filter(field => field.getStatus(businessData) === 'success').length;
    const fieldsWithSuggestions = businessFields.filter(field => 
      optimizationSuggestions.find(s => s.field === field.id || s.type === field.id)
    ).length;
    
    return {
      current: Math.round((completedFields / totalFields) * 100),
      potential: Math.round(((completedFields + fieldsWithSuggestions) / totalFields) * 100),
      suggestions: fieldsWithSuggestions
    };
  };

  const score = calculateOptimizationScore();

  return (
    <Box sx={{ px: 4, py: 0 }}>
      {/* Clean Professional Header */}
      <Box sx={{ mb: 6 }}>
        {/* Title Section */}
        <Box sx={{ mb: 4, px: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a', mb: 2 }}>
            Profil-Optimierung
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', maxWidth: 700, lineHeight: 1.6 }}>
            Verbessern Sie Ihr Google Business Profil systematisch mit KI-gestützten Empfehlungen für bessere lokale Sichtbarkeit
          </Typography>
        </Box>

        {/* Clean Action Bar */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 4,
          mx: 2,
          bgcolor: '#f8f9fa',
          borderRadius: 3,
          border: '1px solid #e9ecef',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          {/* Optimization Score */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 800, color: '#1a1a1a', lineHeight: 1, mb: 1 }}>
                {score.current}%
              </Typography>
              <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                Vollständigkeit
              </Typography>
            </Box>
            
            {score.suggestions > 0 && (
              <>
                <Box sx={{ height: 40, width: 1, bgcolor: '#dee2e6' }} />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: '#2196f3', lineHeight: 1, mb: 1 }}>
                    +{score.potential - score.current}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#2196f3', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Potenzial
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            {score.suggestions > 0 && (
              <Chip 
                label={`${score.suggestions} KI-Verbesserungen`}
                sx={{ 
                  bgcolor: '#e3f2fd', 
                  color: '#1976d2',
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                  fontSize: '0.875rem'
                }}
              />
            )}
            
            {(pendingChangesCountProp !== null ? pendingChangesCountProp : pendingChangesCount) > 0 && (
              <Button
                variant="contained"
                onClick={() => setPendingChangesOpen(true)}
                sx={{
                  bgcolor: '#ff9800',
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                  '&:hover': { 
                    bgcolor: '#f57c00',
                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.4)'
                  }
                }}
              >
                {(pendingChangesCountProp !== null ? pendingChangesCountProp : pendingChangesCount)} Änderung{(pendingChangesCountProp !== null ? pendingChangesCountProp : pendingChangesCount) !== 1 ? 'en' : ''} veröffentlichen
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Clean Professional Table */}
      <Paper sx={{ borderRadius: 2, border: '1px solid #e5e5e5', overflow: 'hidden' }}>
        {/* Priority Section: AI Suggestions */}
        {businessFields.filter(field => 
          !field.excludeFromAI && 
          optimizationSuggestions.find(s => s.field === field.id || s.type === field.id)
        ).length > 0 && (
          <Box sx={{ bgcolor: '#f8f9ff', borderBottom: '1px solid #e5e5e5' }}>
            <Box sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                KI-Optimierungsempfehlungen
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 4 }}>
                Priorisierte Verbesserungen für maximale Wirkung
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {businessFields
                  .filter(field => 
                    !field.excludeFromAI && 
                    optimizationSuggestions.find(s => s.field === field.id || s.type === field.id)
                  )
                  .map((field) => {
                    const value = field.getValue(businessData);
                    const suggestion = optimizationSuggestions.find(s => s.field === field.id || s.type === field.id);
                    
                    return (
                      <Box key={field.id} sx={{ 
                        p: 4, 
                        bgcolor: 'white', 
                        borderRadius: 2, 
                        border: '2px solid #2196f3',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                              <Box sx={{ color: '#2196f3' }}>{field.icon}</Box>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                                {field.label}
                              </Typography>
                              <Chip 
                                label="KI-Empfehlung" 
                                size="small" 
                                sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 500 }}
                              />
                            </Box>
                            
                            <Grid container spacing={3}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" sx={{ color: '#666', mb: 1, fontWeight: 500 }}>
                                  Aktuell:
                                </Typography>
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                  <Typography variant="body2" sx={{ color: '#1a1a1a' }}>
                                    {typeof value === 'object' && value.text ? value.text : value}
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" sx={{ color: '#2196f3', mb: 1, fontWeight: 500 }}>
                                  KI-Empfehlung:
                                </Typography>
                                <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                                  <Typography variant="body2" sx={{ color: '#1a1a1a', fontWeight: 500 }}>
                                    {suggestion.suggestedValue || suggestion.suggestion || suggestion.recommendedValue}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                            
                            {(suggestion.seoReason || suggestion.reason || suggestion.description) && (
                              <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                                <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
                                  Begründung: {suggestion.seoReason || suggestion.reason || suggestion.description}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          
                          <Box sx={{ ml: 4 }}>
                            <Button 
                              variant="contained"
                              onClick={() => handleEdit(field)}
                              sx={{
                                bgcolor: '#2196f3',
                                color: 'white',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              Optimieren
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                }
              </Box>
            </Box>
          </Box>
        )}
        
        {/* All Fields Section */}
        <Box sx={{ p: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
            Alle Profilfelder
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 4 }}>
            Vollständige Übersicht aller Google Business Profil Informationen
          </Typography>
          
          <TableContainer sx={{ border: '1px solid #e5e5e5', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a', py: 2 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a', py: 2 }}>Feld</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a', py: 2 }}>Wert</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#1a1a1a', py: 2, textAlign: 'right' }}>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {businessFields.map((field) => {
                  const value = field.getValue(businessData);
                  const status = field.getStatus(businessData);
                  const suggestion = optimizationSuggestions.find(s => s.field === field.id || s.type === field.id);
                  
                  return (
                    <TableRow 
                      key={field.id}
                      sx={{ 
                        '&:hover': { bgcolor: '#f9f9f9' },
                        borderBottom: '1px solid #f0f0f0'
                      }}
                    >
                      <TableCell sx={{ py: 3, width: 140 }}>
                        {getStatusChip(status, field)}
                      </TableCell>
                      <TableCell sx={{ py: 3, width: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ color: '#666' }}>{field.icon}</Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1a1a1a' }}>
                            {field.label}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 3 }}>
                        <Box sx={{ maxWidth: 400 }}>
                          {renderValue(field, value)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 3, textAlign: 'right' }}>
                        {renderActions(field, value)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {renderEditDialog()}

      {/* Image Gallery */}
      <ImageGallery
        open={imageGalleryOpen}
        onClose={() => setImageGalleryOpen(false)}
        mediaItems={businessData?.media_items || []}
        profilePhoto={businessData?.media_profile}
        coverPhoto={businessData?.media_cover}
        businessName={businessData?.business_name || businessData?.locationName || "Unternehmen"}
      />

      {/* Category Manager */}
      <CategoryManager
        open={categoryManagerOpen}
        onClose={() => setCategoryManagerOpen(false)}
        currentCategories={getCurrentCategories()}
        onSave={handleCategorySave}
        businessData={businessData}
      />

      {/* Attribute Manager */}
      <AttributeManager
        open={attributeManagerOpen}
        onClose={() => setAttributeManagerOpen(false)}
        currentAttributes={getCurrentAttributes()}
        onSave={handleAttributeSave}
        businessData={businessData}
      />

      {/* Pending Changes Manager */}
      <PendingChangesManager
        open={pendingChangesOpen}
        onClose={() => setPendingChangesOpen(false)}
        locationId={locationId}
        onPublishComplete={(result) => {
          console.log('🚀 Publish completed:', result);
          // Reload pending changes count
          loadPendingChangesCount();
          // Optional: Refresh business data
          if (onFieldUpdate && result.success) {
            // Trigger a refresh of the business data if needed
          }
        }}
      />
    </Box>
  );
};

// Business Hours Editor Component - Clean & Übersichtlich
const BusinessHoursEditor = ({ hours, onChange }) => {
  const [localHours, setLocalHours] = useState(hours || {});
  
  const days = [
    { key: 'monday', label: 'Montag', short: 'Mo' },
    { key: 'tuesday', label: 'Dienstag', short: 'Di' },
    { key: 'wednesday', label: 'Mittwoch', short: 'Mi' },
    { key: 'thursday', label: 'Donnerstag', short: 'Do' },
    { key: 'friday', label: 'Freitag', short: 'Fr' },
    { key: 'saturday', label: 'Samstag', short: 'Sa' },
    { key: 'sunday', label: 'Sonntag', short: 'So' }
  ];
  
  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }
  
  const updateDay = (day, field, value) => {
    const newHours = {
      ...localHours,
      [day]: {
        ...localHours[day],
        [field]: value
      }
    };
    setLocalHours(newHours);
    onChange(newHours);
  };
  
  const toggleSplit = (day) => {
    const dayHours = localHours[day] || {};
    const newHours = {
      ...localHours,
      [day]: {
        ...dayHours,
        split: !dayHours.split,
        afternoon_open: dayHours.split ? undefined : '14:00',
        afternoon_close: dayHours.split ? undefined : '18:00'
      }
    };
    setLocalHours(newHours);
    onChange(newHours);
  };
  
  const toggleDayOpen = (day) => {
    const isOpen = localHours[day]?.open;
    if (isOpen) {
      const newHours = { ...localHours };
      delete newHours[day];
      setLocalHours(newHours);
      onChange(newHours);
    } else {
      const newHours = {
        ...localHours,
        [day]: {
          open: '09:00',
          close: '18:00'
        }
      };
      setLocalHours(newHours);
      onChange(newHours);
    }
  };
  
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a1a1a' }}>
        Öffnungszeiten bearbeiten
      </Typography>
      
      <Stack spacing={1}>
        {days.map(({ key, label, short }) => {
          const dayHours = localHours[key] || {};
          const isOpen = !!dayHours.open;
          
          return (
            <Paper
              key={key}
              elevation={0}
              sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: 'white'
              }}
            >
              {/* Kompakter Day Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 1.5,
                backgroundColor: '#fafafa',
                borderBottom: isOpen ? '1px solid #e0e0e0' : 'none'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Switch
                    checked={isOpen}
                    onChange={() => toggleDayOpen(key)}
                    size="small"
                    sx={{
                      mr: 1.5,
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2196f3',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2196f3',
                      },
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: isOpen ? '#1a1a1a' : '#9e9e9e',
                      fontSize: '0.9rem'
                    }}
                  >
                    {label}
                  </Typography>
                </Box>

                {/* Live Preview */}
                {isOpen && dayHours.open && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#1a1a1a',
                      fontWeight: 500,
                      fontSize: '0.85rem'
                    }}
                  >
                    {dayHours.split && dayHours.afternoon_open ? 
                      `${dayHours.open}-${dayHours.close}, ${dayHours.afternoon_open}-${dayHours.afternoon_close}` :
                      `${dayHours.open}-${dayHours.close}`
                    }
                  </Typography>
                )}
              </Box>

              {/* Kompakte Zeit-Konfiguration */}
              {isOpen && (
                <Box sx={{ p: 2 }}>
                  {/* Hauptzeiten in einer Zeile */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'end', mb: dayHours.split ? 2 : 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, mb: 0.5, display: 'block' }}>
                        Öffnung
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={dayHours.open || ''}
                          onChange={(e) => updateDay(key, 'open', e.target.value)}
                          displayEmpty
                        >
                          {timeOptions.map(time => (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, mb: 0.5, display: 'block' }}>
                        Schließung
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={dayHours.close || ''}
                          onChange={(e) => updateDay(key, 'close', e.target.value)}
                          displayEmpty
                        >
                          {timeOptions.map(time => (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    
                    {/* Mittagspause Toggle */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={!!dayHours.split}
                        onChange={() => toggleSplit(key)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#ff9800',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#ff9800',
                          },
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, fontSize: '0.75rem' }}>
                        Mittagspause
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Nachmittag-Zeiten (nur wenn Mittagspause aktiv) */}
                  {dayHours.split && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'end' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, mb: 0.5, display: 'block' }}>
                          Nachmittag
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={dayHours.afternoon_open || ''}
                            onChange={(e) => updateDay(key, 'afternoon_open', e.target.value)}
                            displayEmpty
                          >
                            {timeOptions.map(time => (
                              <MenuItem key={time} value={time}>
                                {time}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, mb: 0.5, display: 'block' }}>
                          Ende
                        </Typography>
                        <FormControl fullWidth size="small">
                          <Select
                            value={dayHours.afternoon_close || ''}
                            onChange={(e) => updateDay(key, 'afternoon_close', e.target.value)}
                            displayEmpty
                          >
                            {timeOptions.map(time => (
                              <MenuItem key={time} value={time}>
                                {time}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        {/* Platzhalter für Alignment */}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default OptimizationTable;